/**
 * Onglet Actions de l'agent : les tables de référence qu'il consulte.
 * Chaque table cochée devient un outil « consulter » pour cet agent ; le
 * contenu des tables se gère dans le menu Tables.
 */
import { useTranslation } from 'react-i18next'
import { Table2 } from 'lucide-react'
import { toast } from 'sonner'
import { useLookupTables, useSaveLookupTable } from '@/api/lookupTables'
import { Switch } from '@/components/ui/switch'
import { useState } from 'react'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { LookupTablesView } from '@/components/manager/LookupTablesView'

export function LookupSection({ companyId, agentId, sansEntete = false }: { companyId: number | undefined; agentId: number | undefined; sansEntete?: boolean }) {
  const { t } = useTranslation()
  // Les tables se gèrent dans un panneau par-dessus la fiche : rien de ce qui
  // est en cours de modification dans l'agent n'est perdu.
  const [panneau, setPanneau] = useState(false)
  const ouvrirTables = () => setPanneau(true)
  const { data: tables } = useLookupTables(companyId)
  const save = useSaveLookupTable(companyId)

  const basculer = async (id: number, on: boolean) => {
    const table = tables?.find((x) => x.id === id)
    if (!table || !agentId) return
    const agent_ids = on ? [...table.agent_ids, agentId] : table.agent_ids.filter((a) => a !== agentId)
    try {
      await save.mutateAsync({ id, data: { ...table, agent_ids } })
    } catch {
      toast.error(t('manager.lookup.errSave', 'Impossible d’enregistrer'))
    }
  }

  return (
    <section>
      <Sheet open={panneau} onOpenChange={setPanneau}>
        <SheetContent side="right" className="!w-full !max-w-none p-0 overflow-hidden">
          <div className="h-full flex flex-col">
            <div className="px-6 pt-6 pb-4 border-b dark:border-slate-800 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-500/20 rounded-lg">
                    <Table2 className="h-5 w-5 text-blue-600" />
                  </div>
                  {t('manager.aiAgents.actions.lookupTitle', 'Tables de référence')}
                </SheetTitle>
              </SheetHeader>
            </div>
            <div className="flex-1 min-h-0">
              <LookupTablesView />
            </div>
          </div>
        </SheetContent>
      </Sheet>
      <div className={sansEntete ? 'flex justify-end mb-3' : 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4'}>
        {!sansEntete && (
        <div>
          <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
            <Table2 className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            {t('manager.aiAgents.actions.lookupTitle', 'Tables de référence')}
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 mt-1">
            {t('manager.aiAgents.actions.lookupDescription', 'Prix, tarifs, disponibilités : l’agent consulte la table au lieu de deviner, lit la réponse telle quelle et l’envoie par texto.')}
          </p>
        </div>
        )}
        <button type="button" onClick={ouvrirTables} className="text-sm font-medium text-blue-600 hover:underline whitespace-nowrap">
          {t('manager.aiAgents.actions.lookupManage', 'Gérer les tables')}
        </button>
      </div>
      {!agentId ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('manager.aiAgents.actions.lookupAfterCreate', 'Enregistrez l’agent pour lui associer des tables.')}</p>
      ) : !tables?.length ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('manager.aiAgents.actions.lookupNone', 'Aucune table pour l’instant. Créez-en une dans Tables de référence.')}</p>
      ) : (
        <div className="border dark:border-slate-800 rounded-lg overflow-hidden bg-white dark:bg-slate-900 divide-y divide-gray-100 dark:divide-slate-800">
          {tables.map((tb) => {
            const on = tb.agent_ids.includes(agentId)
            return (
              <label key={tb.id} className="flex items-center justify-between gap-4 px-4 py-3 cursor-pointer">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">{tb.name}</p>
                  <p className="text-xs text-slate-500 truncate">{tb.usage_hint} · {tb.rows_count} {t('manager.lookup.rowsWord', 'lignes')}</p>
                </div>
                <Switch checked={on} disabled={save.isPending} onCheckedChange={(v) => basculer(tb.id, v)} />
              </label>
            )
          })}
        </div>
      )}
    </section>
  )
}
